import { getBaseRole } from '@/helpers/customRoles';
import {BalanceIcon} from "@/utils/icons";
import {convertNumberCost} from "@/helpers/convertNumberCost";
import s from './index.module.css';
import { selectProfile } from "@store/profile.reducer";
import { useSelector } from "react-redux";
import Preloader from "@UI/Preloader";
import { useGetIsShowBalanceOfAdditionalActivitiesInLeftMenu } from "@pages/Admin/PlatformSettings2/ScheduleClasses/hooks/useGetIsShowBalanceOfAdditionalActivitiesInLeftMenu";
import Button from "@UI/New/Button";
import { useHistory } from "react-router-dom";
import { useLocation } from "react-router-dom";
import {useEffect, useMemo, useRef, useState} from "react";
import {usersAPI} from "@/api";

const parseStudentBalances = (value) => {
    if (Array.isArray(value)) return value;

    try {
        const parsed = JSON.parse(value);
        return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
        return [];
    }
};

const BalanceBlock = ({type = 'general'}) => {
    const history = useHistory();
    const location = useLocation();
    const profile = useSelector(selectProfile)
    const parentSelectedChild = useSelector(state => state.parentSelectedChild)
    const parentSelectedChildId = useSelector(state => state.parentSelectedChildId)
    const settings = useSelector(state => state.settings)
    const balanceRefreshVersion = useSelector(
        state => state.balanceOfAdditionalActivities?.refreshVersion ?? 0
    );
    const loadingSettings = settings?.status < 2;
    const isShowBalanceOfAdditionalActivitiesInLeftMenu = useGetIsShowBalanceOfAdditionalActivitiesInLeftMenu();
    const studentBalances = useMemo(
        () =>
            parseStudentBalances(settings?.studentBalanceSettings)
                .map((balance, index) => ({
                    id: String(balance?.id ?? balance?.balanceId ?? index),
                    name: balance?.name || `Баланс ${index + 1}`,
                }))
                .filter((balance) => balance.id),
        [settings?.studentBalanceSettings],
    );
    const [studentBalanceValues, setStudentBalanceValues] = useState({});
    const [studentBalancesLoading, setStudentBalancesLoading] = useState(false);
    const [studentBalancesError, setStudentBalancesError] = useState(false);
    const [activeBalanceIndex, setActiveBalanceIndex] = useState(0);
    const touchStartXRef = useRef(null);
    const studentId = getBaseRole(profile.role) === 'parent' ? parentSelectedChildId : profile?.id;
    const activeBalance = studentBalances[activeBalanceIndex] ?? studentBalances[0];

    useEffect(() => {
        if (activeBalanceIndex > Math.max(studentBalances.length - 1, 0)) {
            setActiveBalanceIndex(0);
        }
    }, [activeBalanceIndex, studentBalances.length]);

    const changeActiveBalance = (direction) => {
        if (studentBalances.length <= 1) return;
        setActiveBalanceIndex((prev) => {
            const next = prev + direction;
            if (next < 0) return studentBalances.length - 1;
            if (next >= studentBalances.length) return 0;
            return next;
        });
    };

    const onTouchStart = (event) => {
        if (studentBalances.length <= 1) return;
        touchStartXRef.current = event.touches?.[0]?.clientX ?? null;
    };

    const onTouchEnd = (event) => {
        if (touchStartXRef.current === null || studentBalances.length <= 1) return;
        const endX = event.changedTouches?.[0]?.clientX;
        if (endX === undefined) {
            touchStartXRef.current = null;
            return;
        }
        const diff = endX - touchStartXRef.current;
        if (Math.abs(diff) > 35) {
            changeActiveBalance(diff > 0 ? -1 : 1);
        }
        touchStartXRef.current = null;
    };

    useEffect(() => {
        if (
            type !== 'additionalActivities' ||
            !isShowBalanceOfAdditionalActivitiesInLeftMenu ||
            !studentId ||
            !studentBalances.length
        ) {
            return;
        }

        let cancelled = false;
        setStudentBalancesLoading(true);
        setStudentBalancesError(false);

        Promise.all(
            studentBalances.map((balance) =>
                usersAPI
                    .getBalanceOfAdditionalActivities({
                        user_id: studentId,
                        balance_id: balance.id,
                    })
                    .then(({data}) => [balance.id, data?.status === 'success' ? data?.data ?? 0 : 0])
            )
        )
            .then((rows) => {
                if (cancelled) return;
                setStudentBalanceValues(Object.fromEntries(rows));
            })
            .catch(() => {
                if (cancelled) return;
                setStudentBalancesError(true);
            })
            .finally(() => {
                if (cancelled) return;
                setStudentBalancesLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [
        isShowBalanceOfAdditionalActivitiesInLeftMenu,
        balanceRefreshVersion,
        studentBalances,
        studentId,
        type,
    ]);

    const getBalance = () => {
        if(type === 'general'){
            return getBaseRole(profile.role) === 'parent' ? parentSelectedChild.data.balance ?? 0 : profile.balance ?? 0
        }
        return 0
    }

    const balance = getBalance();

    if(getBaseRole(profile.role) === 'parent' && !parentSelectedChildId) return <></>;
    if(loadingSettings) return <></>;
    if(type === 'general') return <></>;
    if(type === 'general' && settings?.isUniversalBalance !== '1') return <></>;
    if(type === 'general' && (settings?.isFreeCourses === '1' || (getBaseRole(profile.role) !== 'children' && getBaseRole(profile.role) !== 'parent'))) return <></>;
    
    if(type === 'additionalActivities' && (studentBalancesError || !isShowBalanceOfAdditionalActivitiesInLeftMenu || !studentBalances.length)) return <></>;
    if(type === 'additionalActivities' && (getBaseRole(profile.role) !== 'children' && getBaseRole(profile.role) !== 'parent')) return <></>;



    return (
        <div
            className={s.balanceBlock}
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
            onTouchCancel={() => {
                touchStartXRef.current = null;
            }}
        >
            <div className={s.balanceWrapper}>
                {
                    (
                        (parentSelectedChild.status === 'loading' && type === 'general') || 
                        (studentBalancesLoading && type === 'additionalActivities')) ? (
                            <div style={{margin: 'auto'}}>
                                <Preloader />
                            </div>
                    ) : (
                        <>
                            <div className={s.balanceIcon}>
                                <BalanceIcon/>
                            </div>
                            <div className={s.balance}>
                                <div className={s.balanceCount}>
                                    {type === 'general' ? 'Баланс' : activeBalance?.name}
                                    {type === 'general' ? (
                                        <div className={s.money}>{convertNumberCost(balance ?? 0)} ₽</div>
                                    ) : (
                                        <div className={s.money}>
                                            {convertNumberCost(studentBalanceValues[activeBalance?.id] ?? 0)} ₽
                                        </div>
                                    )}
                                </div>
                                <div className={s.balanceFooter}>
                                    {
                                        type === 'general' &&  (
                                            <>
                                                <span>0 €</span>
                                                <span>0 $</span>
                                            </>
                                        )
                                    }
                                    {
                                        type === 'additionalActivities' && !location.pathname?.includes('/payment') && (
                                            <Button
                                                className={s.button}
                                                onClick={()=>{
                                                    history.push('/payment', {
                                                        openModalReplenishmentAdditionalActivities: true,
                                                        balanceId: activeBalance?.id,
                                                    });
                                                }}
                                            >Пополнить</Button>
                                        )
                                    }
                                </div>
                                {type === 'additionalActivities' && studentBalances.length > 1 && (
                                    <>
                                        <button
                                            type="button"
                                            className={`${s.carouselArrow} ${s.carouselArrowLeft}`}
                                            onClick={() => changeActiveBalance(-1)}
                                            aria-label="Предыдущий баланс"
                                        >
                                            ‹
                                        </button>
                                        <button
                                            type="button"
                                            className={`${s.carouselArrow} ${s.carouselArrowRight}`}
                                            onClick={() => changeActiveBalance(1)}
                                            aria-label="Следующий баланс"
                                        >
                                            ›
                                        </button>
                                        <div className={s.carouselControls}>
                                            <div className={s.dots}>
                                                {studentBalances.map((item, index) => (
                                                    <button
                                                        key={item.id}
                                                        type="button"
                                                        aria-label={`Баланс ${index + 1}`}
                                                        className={`${s.dot} ${index === activeBalanceIndex ? s.dotActive : ''}`}
                                                        onClick={() => setActiveBalanceIndex(index)}
                                                    />
                                                ))}
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                        </>
                    )
                }
            </div>
        </div>
    )
}

export default BalanceBlock;
